import type {
    FC,
    MouseEvent as ReactMouseEvent,
    PointerEvent as ReactPointerEvent,
    ReactNode
} from 'react'
import type { Pillar } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { PillarCard } from '@/components/content/pillar-card'

const AUTOPLAY_MS = 5000
const DRAG_THRESHOLD = 60

interface PillarSliderProps {
    pillars: Pillar[]
    className?: string
}

export const PillarSlider: FC<PillarSliderProps> = ({ pillars, className }): ReactNode => {
    const count = pillars.length
    const slides = count > 0 ? [...pillars, ...pillars, ...pillars] : []

    const [index, setIndex] = useState(count)
    const [step, setStep] = useState(0)
    const [noTransition, setNoTransition] = useState(false)
    const [paused, setPaused] = useState(false)
    const [reduced, setReduced] = useState(false)
    const [drag, setDrag] = useState(0)

    const trackRef = useRef<HTMLDivElement>(null)
    const slideRef = useRef<HTMLDivElement>(null)
    const pointerRef = useRef<{ id: number; x: number } | null>(null)
    const movedRef = useRef(false)

    const measure = useCallback(() => {
        const slide = slideRef.current
        if (!slide) return
        setStep(slide.getBoundingClientRect().width)
    }, [])

    useEffect(() => {
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [measure])

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)')
        setReduced(query.matches)
        const onChange = (event: MediaQueryListEvent) => setReduced(event.matches)
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [])

    useEffect(() => {
        if (paused || reduced || step === 0 || count < 2) return
        const id = window.setInterval(() => setIndex((value) => value + 1), AUTOPLAY_MS)
        return () => window.clearInterval(id)
    }, [paused, reduced, step, count])

    useEffect(() => {
        if (!noTransition) return
        const id = window.requestAnimationFrame(() => setNoTransition(false))
        return () => window.cancelAnimationFrame(id)
    }, [noTransition])

    const normalize = () => {
        if (count === 0) return
        if (index >= count * 2) {
            setNoTransition(true)
            setIndex(index - count)
        } else if (index < count) {
            setNoTransition(true)
            setIndex(index + count)
        }
    }

    const go = (delta: number) => setIndex((value) => value + delta)

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        pointerRef.current = { id: event.pointerId, x: event.clientX }
        movedRef.current = false
        setPaused(true)
    }

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const pointer = pointerRef.current
        if (!pointer || pointer.id !== event.pointerId) return
        const delta = event.clientX - pointer.x
        if (Math.abs(delta) > 6) movedRef.current = true
        setDrag(delta)
    }

    const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        const pointer = pointerRef.current
        if (!pointer || pointer.id !== event.pointerId) return
        pointerRef.current = null
        const delta = drag
        setDrag(0)
        setPaused(false)
        if (Math.abs(delta) > DRAG_THRESHOLD) go(delta < 0 ? 1 : -1)
    }

    const onClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!movedRef.current) return
        event.preventDefault()
        event.stopPropagation()
        movedRef.current = false
    }

    if (count === 0) return null

    const active = ((index % count) + count) % count
    const offset = -(index * step) + drag

    return (
        <div
            className={cn('relative', className)}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div
                className='overflow-hidden'
                role='region'
                aria-roledescription='carousel'
                aria-label='Pillar guides'
            >
                <div
                    ref={trackRef}
                    className={cn(
                        '-ml-6 flex touch-pan-y select-none will-change-transform',
                        noTransition || drag !== 0
                            ? 'transition-none'
                            : 'transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]'
                    )}
                    style={{ transform: `translate3d(${offset}px, 0, 0)` }}
                    onTransitionEnd={normalize}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClickCapture={onClickCapture}
                >
                    {slides.map((pillar, position) => (
                        <div
                            key={`${pillar.id}-${position}`}
                            ref={position === 0 ? slideRef : undefined}
                            className='w-full shrink-0 grow-0 basis-full pl-6 sm:basis-1/2 lg:basis-1/3'
                            aria-hidden={
                                position < count || position >= count * 2 ? true : undefined
                            }
                        >
                            <PillarCard pillar={pillar} className='h-full' />
                        </div>
                    ))}
                </div>
            </div>

            <div className='mt-8 flex items-center justify-center gap-4'>
                <button
                    type='button'
                    onClick={() => go(-1)}
                    aria-label='Previous guide'
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                >
                    <ChevronLeft className='size-5' />
                </button>

                <div className='flex flex-wrap items-center justify-center gap-2'>
                    {pillars.map((pillar, position) => (
                        <button
                            key={pillar.id}
                            type='button'
                            onClick={() => go(position - active)}
                            aria-label={`Go to ${pillar.name}`}
                            aria-current={position === active ? 'true' : undefined}
                            className={cn(
                                'h-2 rounded-full transition-all duration-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                                position === active
                                    ? 'w-8 bg-primary'
                                    : 'w-2 bg-border hover:bg-primary/40'
                            )}
                        />
                    ))}
                </div>

                <button
                    type='button'
                    onClick={() => go(1)}
                    aria-label='Next guide'
                    className='flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                >
                    <ChevronRight className='size-5' />
                </button>
            </div>
        </div>
    )
}
